/*
  grove-eggs.js — a hidden easter egg, planted by a friend of the house. 🌱

  One secret, zero UI footprint until discovered:
  Type "grove" anywhere (outside a form field) → leaves drift across the
  viewport for ~10s. Fast repeats stack extra bursts.

  Self-contained: injects its own CSS, no dependencies, respects
  prefers-reduced-motion. Loaded deferred via snippets/grove-eggs.liquid.
*/
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const CSS = `
    .ge-leaves {
      position: fixed;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
      z-index: 90;
    }
    .ge-leaf {
      position: absolute;
      top: -40px;
      left: var(--x);
      width: var(--s);
      height: var(--s);
      animation: ge-fall var(--d) linear var(--delay) both;
    }
    .ge-leaf__sway {
      display: block;
      width: 100%;
      height: 100%;
      animation: ge-sway var(--sway) ease-in-out infinite alternate;
    }
    .ge-leaf svg { display: block; width: 100%; height: 100%; }
    @keyframes ge-fall {
      from { transform: translateY(0) rotate(0deg); opacity: 1; }
      to { transform: translateY(108vh) rotate(300deg); opacity: 1; }
    }
    @keyframes ge-sway {
      from { transform: translateX(calc(var(--amp) * -1)); }
      to { transform: translateX(var(--amp)); }
    }
    @keyframes ge-fade {
      0% { opacity: 0; }
      25%, 70% { opacity: 1; }
      100% { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .ge-leaf { top: var(--t); animation: ge-fade 5s ease var(--delay) both; }
      .ge-leaf__sway { animation: none; }
    }
  `;

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  const LEAF_SVG = (color) =>
    `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 2C6.5 8.5 4.5 15 12 22c7.5-7 5.5-13.5 0-20Z" fill="${color}"/>
      <path d="M12 21C11.2 15.5 11.2 9.5 12 4" fill="none" stroke="rgba(255,255,255,0.4)" stroke-width="0.8"/>
    </svg>`;
  // ---- Leaf tuning — all the knobs live here ----
  const LEAVES = {
    count: 84, // leaves per burst
    size: [18, 36], // px, [min, max]
    fall: [5, 9], // s to cross the viewport, [min, max]
    stagger: 5, // s — each leaf starts within 0..stagger
    sway: [1.6, 3], // s per side-to-side swing, [min, max]
    swayAmp: [10, 36], // px swing width, [min, max]
    lifetime: 14.5, // s until a burst is removed (≥ fall max + stagger)
    colors: ['#8a9a7b', '#a3b18a', '#6b7f5e', '#588157'],
  };
  const rand = ([min, max]) => min + Math.random() * (max - min);

  // Each trigger gets its own burst so fast repeats stack.
  function fallLeaves() {
    const container = document.createElement('div');
    container.className = 'ge-leaves';
    container.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < LEAVES.count; i += 1) {
      const leaf = document.createElement('span');
      leaf.className = 'ge-leaf';
      leaf.style.setProperty('--x', `${Math.random() * 100}%`);
      leaf.style.setProperty('--t', `${10 + Math.random() * 70}%`);
      leaf.style.setProperty('--s', `${rand(LEAVES.size)}px`);
      leaf.style.setProperty('--d', `${rand(LEAVES.fall)}s`);
      leaf.style.setProperty('--delay', `${Math.random() * LEAVES.stagger}s`);
      leaf.style.setProperty('--sway', `${rand(LEAVES.sway)}s`);
      leaf.style.setProperty('--amp', `${rand(LEAVES.swayAmp)}px`);
      const sway = document.createElement('span');
      sway.className = 'ge-leaf__sway';
      sway.innerHTML = LEAF_SVG(LEAVES.colors[i % LEAVES.colors.length]);
      leaf.appendChild(sway);
      container.appendChild(leaf);
    }
    document.body.appendChild(container);
    setTimeout(() => {
      container.remove();
    }, reducedMotion ? 6000 : LEAVES.lifetime * 1000);
  }

  // Type "grove" anywhere on the page — but not inside a form field, so the
  // search box and newsletter input stay egg-free.
  const WORD = 'grove';
  let typed = '';
  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (
      target &&
      (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
    ) {
      typed = '';
      return;
    }
    if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
    typed = (typed + event.key.toLowerCase()).slice(-WORD.length);
    if (typed === WORD) {
      typed = '';
      fallLeaves();
    }
  });
})();
